import {useLazyQuery, useMutation} from '@apollo/client';
import {logMutations} from 'api';
import {logsQueries} from 'api/graphql-queries/logs/logs.queries';
import {useSessionRedux} from 'hooks/auth';
import {
    LogDetail,
    LogDetailResponse,
    LogFirstDefaultRequest,
    LogInputModel,
    LOGS_CONFIG_ENUM,
    LOGS_EVENT_TYPE,
    LogsGetAllRequest,
    LogsPaginationResponse,
    LogsResponseModel,
} from 'models/logs';
import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';

export function useGetLogsByObjectIdAndObjectType<T>() {
    const [state, setState] = useState<LogsPaginationResponse<T> | undefined>();
    const [refetch, {data, loading}] = useLazyQuery<any, LogsGetAllRequest>(
        logsQueries.ALL_LOGS_BY_OBJECT_ID_AND_OBJECT_TYPE,
        {
            fetchPolicy: 'no-cache',
        },
    );

    useEffect(() => {
        if (data && data.allLogsByObjectIdAndObjectType) {
            setState({
                allLogsByObjectIdAndObjectType: {
                    pageInfo: data.allLogsByObjectIdAndObjectType.pageInfo,
                    nodes: data.allLogsByObjectIdAndObjectType.nodes.map((item: any) => {
                        return {
                            responseTime: item.responseTime,
                            date: item.date,
                            event: item.event,
                            objectId: item.objectId,
                            objectType: item.objectType,
                            requestType: item.requestType,
                            type: item.type,
                            url: item.url,
                            userId: item.userId,
                            data: JSON.parse(item.data) as LogsResponseModel<T>,
                        } as LogDetail;
                    }),
                    edges: data.allLogsByObjectIdAndObjectType.edges,
                    totalCount: data.allLogsByObjectIdAndObjectType.totalCount,
                },
            });
        }
    }, [data]);

    return {
        logsByObjectIdAndObjectTypeResponse: state?.allLogsByObjectIdAndObjectType,
        logsByObjectIdAndObjectTypeLoading: loading,
        getAllLogs: refetch,
    };
}

export function useGetFirstLogByObjectIdAndObjectType<T>() {
    const [state, setState] = useState<LogDetailResponse<T> | undefined>(undefined);
    const [refetch, {data, loading}] = useLazyQuery<any, LogFirstDefaultRequest>(
        logsQueries.FIRST_LOG_BY_OBJECT_ID_AND_OBJECT_TYPE,
        {
            fetchPolicy: 'no-cache',
        },
    );

    useEffect(() => {
        if (data && data.firstLogByObjectIdAndObjectType) {
            setState({
                responseTime: data.firstLogByObjectIdAndObjectType.responseTime,
                id: data.firstLogByObjectIdAndObjectType.id,
                date: data.firstLogByObjectIdAndObjectType.date,
                data: JSON.parse(data.firstLogByObjectIdAndObjectType.data) as LogsResponseModel<T>,
                event: data.firstLogByObjectIdAndObjectType.event,
                active: true,
                objectId: data.firstLogByObjectIdAndObjectType.objectId,
                objectType: data.firstLogByObjectIdAndObjectType.objectType,
                requestType: data.firstLogByObjectIdAndObjectType.requestType,
                type: data.firstLogByObjectIdAndObjectType.type,
                url: data.firstLogByObjectIdAndObjectType.url,
                userId: data.firstLogByObjectIdAndObjectType.userId,
            });
        }
    }, [data]);

    return {
        firstLogByObjectIdAndObjectTypeResponse: state,
        firstLogByObjectIdAndObjectTypeLoading: loading,
        getFirstLog: refetch,
    };
}

export const useCreateLogApi = () => {
    const [create, {loading, data}] = useMutation<null, LogInputModel>(logMutations.CREATE_LOG);
    const {userId} = useSelector(sessionSelector);
    const createLog = useCallback(
        (type: LOGS_CONFIG_ENUM, input: string, logType: LOGS_EVENT_TYPE, objectId: string, objectType: string) => {
            create({
                variables: {
                    type,
                    input: {
                        id: '00000000-0000-0000-0000-000000000000',
                        data: input,
                        responseTime: 10,
                        date: new Date().toISOString(),
                        type: logType.toString(),
                        event: logType.toString() + 'Event',
                        userId: userId,
                        url: window.location.href,
                        requestType: 'POST',
                        active: true,
                        objectId: objectId,
                        objectType: objectType,
                    },
                },
            });
        },
        [],
    );

    return {
        createLog,
        createLogLoading: loading,
        createLogResponse: data,
    };
};
